/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OfferLetterFormsService {

    /**
     * Send Offer form definition for the applicant's offer stage
     * @param applicantExternalId
     * @param offerLetterTemplateId external_id of an explicit template pick; defaults to the one matching the funnel employment_type
     * @param stageId external_id of the offer (document-signing) stage; defaults to the applicant current stage
     * @returns any returns the form definition + approval state
     * @throws ApiError
     */
    public static getInternalApiApplicantsOfferLetterForm(
        applicantExternalId: string,
        offerLetterTemplateId?: string,
        stageId?: string,
    ): CancelablePromise<{
        offer_letter_template?: {
            /**
             * external_id (UUID)
             */
            id?: string;
            title?: string;
        } | null;
        available_templates?: Array<{
            /**
             * external_id (UUID)
             */
            id?: string;
            title?: string;
            /**
             * the applicant's most recent non-deleted offer for this template, or null when none exists yet. Once the applicant has signed one, a newer draft, failed send or canceled offer no longer counts. A sent offer from this stage that the recruiter can still cancel outranks a newer one, and an unsent revision never counts.
             */
            offer?: {
                /**
                 * external_id (UUID) of the OfferLetter
                 */
                external_id?: string;
                /**
                 * the OfferLetter status verbatim — the surface derives its display state from this
                 */
                status?: 'draft' | 'pending_approval' | 'approved' | 'ready_to_send' | 'send_failed' | 'sent' | 'completed' | 'canceled';
                updated_at?: string;
            } | null;
        }>;
        fields?: Array<{
            /**
             * the data key's own type (text, date, boolean, …) — decides a date picker, a Yes/No pair or a text control. null when the field is not mapped to a data key
             */
            key_type?: string | null;
            /**
             * the combined options of every data field for the key on the applicant's opening, to list in a combobox that also accepts a typed value. null when the opening has none, in which case render free text
             */
            options?: Array<{
                value?: string;
                label?: string;
            }> | null;
            /**
             * whether a sent value is saved to the applicant, by the same rule the send applies. null for a field mapped to nothing
             */
            written_back?: boolean | null;
            /**
             * the record that keeps its value when an edit is not written back; null when the value is computed and belongs to no record
             */
            source?: 'applicant' | 'position' | 'location' | 'location_group' | 'opening' | null;
            /**
             * what that record holds now; blank when it holds nothing
             */
            source_value?: string | null;
            key?: string;
            label?: string | null;
            kind?: 'auto_readonly' | 'manual_entry' | 'free_text';
            data_type?: 'text' | 'number' | 'currency' | 'date' | 'address' | 'boolean' | 'select';
            currency_format?: boolean;
            /**
             * currency fields only: the letter also renders the written-out amount
             */
            spell_out?: boolean;
            /**
             * number/currency fields only: authored expressions referencing another field by its key, e.g. ["WEEKLY_SALARY * 2"] (AXHE-4543); empty when the value is entered or resolved from Fountain instead
             */
            formulas?: Array<string>;
            /**
             * the HM can edit every field, even pre-filled ones
             */
            editable?: boolean;
            /**
             * true when the value was resolved from Fountain data
             */
            prefilled?: boolean;
            /**
             * raw (not spelled-out) value; blank for empty manual fields
             */
            value?: string;
            /**
             * the data key this field writes through to, when mapped; editing it also moves any conditional section keyed on it (AXHE-4001)
             */
            data_key?: string | null;
            /**
             * the closed value set to render as a select (AXHE-4180): a `select` field's own authored options, else — for an untyped MAPPED field — the values the template's conditions compare its data key against. null when neither applies, in which case render a free input
             */
            allowed_values?: Array<string> | null;
            /**
             * whether the form is currently asking for this field, resolved under the submitted values. False only when every occurrence of it sits under an excluded conditional_block (see block_occurrences). A hidden field is never reported in missing_fields; a value submitted for it IS kept and stored on the offer, because such a field renders nothing and so cannot leak one into the document (AXHE-4474).
             */
            visible?: boolean;
            /**
             * Reserved for compatibility and always empty. Fields are always included in the form; conditional content is controlled by `conditional_blocks`.
             */
            visible_when?: Array<{
                data_key?: string;
                operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
                /**
                 * null for the presence-only operators; comma-separated entries for in/not_in
                 */
                value?: string | null;
            }>;
            /**
             * where this variable appears in the document: one entry per textual occurrence, each the chain of conditional_blocks keys wrapping it, outermost first — an empty chain is an occurrence at top level. This is the whole of the field-visibility rule (AXHE-4474): the field is withdrawn only when EVERY occurrence sits under at least one excluded block, so a variable used both inside a section and outside it is still asked for. Empty (no occurrences at all) means the field cannot be withdrawn.
             */
            block_occurrences?: Array<Array<string>>;
        }>;
        condition_fields?: Array<{
            /**
             * the data key's own type (text, date, boolean, …) — decides a date picker, a Yes/No pair or a text control. null when the field is not mapped to a data key
             */
            key_type?: string | null;
            /**
             * the combined options of every data field for the key on the applicant's opening, to list in a combobox that also accepts a typed value. null when the opening has none, in which case render free text
             */
            options?: Array<{
                value?: string;
                label?: string;
            }> | null;
            /**
             * whether a sent value is saved to the applicant, by the same rule the send applies. null for a field mapped to nothing
             */
            written_back?: boolean | null;
            /**
             * the record that keeps its value when an edit is not written back; null when the value is computed and belongs to no record
             */
            source?: 'applicant' | 'position' | 'location' | 'location_group' | 'opening' | null;
            /**
             * what that record holds now; blank when it holds nothing
             */
            source_value?: string | null;
            /**
             * the data key
             */
            key?: string;
            /**
             * the data key's title; the humanized key when the account holds no such key
             */
            label?: string | null;
            kind?: 'condition';
            /**
             * the distinct values this template's conditions compare the key against — render as a select; an in condition contributes each of its comma-separated entries. null when they can't be enumerated (a contains/greater_than/less_than/not_in condition is in play), in which case render a free input
             */
            allowed_values?: Array<string> | null;
            editable?: boolean;
            /**
             * true when the value was resolved from document-merge data (applicant, opening, or offer) rather than a previous entry
             */
            prefilled?: boolean;
            value?: string;
        }>;
        conditional_blocks?: Array<{
            key?: string;
            label?: string | null;
            /**
             * whether this section renders under the current values
             */
            included?: boolean;
            /**
             * OR-combined rules behind `included`, shipped so the client can re-resolve which sections are in as the recruiter types. Empty means the block carries no conditions and is excluded, because an unconfigured block is incomplete authoring.
             */
            included_when?: Array<{
                data_key?: string;
                operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
                /**
                 * null for the presence-only operators; comma-separated entries for in/not_in
                 */
                value?: string | null;
                /**
                 * in/not_in only: the list's entries, decoded. Read these rather than splitting `value`, which holds a JSON array when an entry contains a comma. null for every other operator
                 */
                values?: Array<string> | null;
            }>;
        }>;
        /**
         * what earlier sessions on the offer this form resumes reported changing; empty lists when it resumes none. Where several fields map to one data key, the copies named here decide its sections, so the client resolves sections under this report joined with its own session's changes
         */
        edited_keys?: {
            /**
             * template field keys
             */
            field_values?: Array<string>;
            /**
             * condition data keys
             */
            condition_values?: Array<string>;
        };
        approval?: {
            required?: boolean;
            triggered_rules?: Array<{
                label?: string;
                description?: string;
                /**
                 * field the guardrail attaches to; null = offer-wide
                 */
                field_key?: string | null;
                /**
                 * true when a value on the offer tripped the rule; false when the rule matches whatever the recruiter enters
                 */
                conditional?: boolean;
            }>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/applicants/{applicant_external_id}/offer_letter_form',
            path: {
                'applicant_external_id': applicantExternalId,
            },
            query: {
                'offer_letter_template_id': offerLetterTemplateId,
                'stage_id': stageId,
            },
        });
    }

    /**
     * Evaluate approval guardrails + required-field readiness for submitted values
     * @param applicantExternalId
     * @param requestBody
     * @returns any returns approval state + missing required fields
     * @throws ApiError
     */
    public static postInternalApiApplicantsOfferLetterFormPreview(
        applicantExternalId: string,
        requestBody: {
            /**
             * external_id of the selected template
             */
            offer_letter_template_id?: string;
            /**
             * external_id of the offer stage (optional)
             */
            stage_id?: string;
            /**
             * field key -> entered value
             */
            field_values?: Record<string, string>;
            /**
             * data key -> value the recruiter entered for a key driving a conditional section. Absent keys resolve from document-merge data (applicant, opening, or offer) (AXHE-4001)
             */
            condition_values?: Record<string, string>;
            /**
             * which controls this session moved off the value the form displayed. Joined with the report of the offer the form resumes, it picks which of several fields mapped to one data key decides that key's sections, as the offer create and update do
             */
            edited_keys?: {
                /**
                 * template field keys
                 */
                field_values?: Array<string>;
                /**
                 * condition data keys
                 */
                condition_values?: Array<string>;
            };
        },
    ): CancelablePromise<{
        approval?: {
            required?: boolean;
            triggered_rules?: Array<{
                label?: string;
                description?: string;
                /**
                 * field the guardrail attaches to; null = offer-wide
                 */
                field_key?: string | null;
                /**
                 * true when a value on the offer tripped the rule; false when the rule matches whatever the recruiter enters
                 */
                conditional?: boolean;
            }>;
        };
        missing_fields?: Array<{
            key?: string;
            /**
             * why this key blocks generation. unresolvable_mapping / blank_value / missing_value name a field in `fields` that needs a value. unresolvable_signer names a signature placeholder whose role is not on the template's signer roster — there is no entry in `fields` for it and no value that resolves it, so surface it as a document-level block, not as a missing field.
             */
            reason?: 'unresolvable_mapping' | 'blank_value' | 'missing_value' | 'unresolvable_signer';
        }>;
        /**
         * false blocks the recruiter from proceeding
         */
        can_generate?: boolean;
        /**
         * sections resolved under the SUBMITTED values. The GET response carries its own copy, but that one reflects page-load defaults and never changes as the recruiter edits — read this one after a check.
         */
        conditional_blocks?: Array<{
            key?: string;
            label?: string | null;
            /**
             * whether this section renders under the current values
             */
            included?: boolean;
            /**
             * OR-combined rules behind `included`, shipped so the client can re-resolve which sections are in as the recruiter types. Empty means the block carries no conditions and is excluded, because an unconfigured block is incomplete authoring.
             */
            included_when?: Array<{
                data_key?: string;
                operator?: 'equals' | 'not_equals' | 'present' | 'blank' | 'greater_than' | 'less_than' | 'contains' | 'in' | 'not_in';
                /**
                 * null for the presence-only operators; comma-separated entries for in/not_in
                 */
                value?: string | null;
                /**
                 * in/not_in only: the list's entries, decoded. Read these rather than splitting `value`, which holds a JSON array when an entry contains a comma. null for every other operator
                 */
                values?: Array<string> | null;
            }>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/applicants/{applicant_external_id}/offer_letter_form/preview',
            path: {
                'applicant_external_id': applicantExternalId,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}